import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import type { GridData, GridMetadata } from 'models/forms/fields';
import { ExportGridButtonMetadata } from 'models/forms/fields/ExportGridButtonMetadata';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';
import { DownloadButton } from './DownloadButton';
import { exportCSV } from './exportCSV';
import { useFilterVisibleHeaders } from './GridField';

const emptyGridData: GridData = [];

export const ExportGridButton: FC<ProductNodeBuilderProps> = ({
  node,
  quoteWorkspaceVersion,
  mustForceAdvancedMode,
  tabIndex,
}) => {
  const metadata = useFieldMetadata<ExportGridButtonMetadata>(node.id);
  const id = metadata?.gridId ?? node.id;

  const { headers: unfilteredHeaders, hasRowIndexColumn } = useFieldMetadata<GridMetadata>(id)!;

  const headers = useFilterVisibleHeaders(unfilteredHeaders, quoteWorkspaceVersion, mustForceAdvancedMode);
  const nbColumns = headers?.length ?? 0;
  const [data = emptyGridData] = useData<GridData>(id);
  const gridData = useMemo(() => data || emptyGridData, [data]);
  const nbRows = gridData.length;

  const intl = useIntl();

  const onDownload = useCallback(() => {
    if (headers !== undefined) {
      exportCSV(id, headers, gridData, hasRowIndexColumn, intl);
    }
  }, [headers, id, gridData, hasRowIndexColumn, intl]);

  const hasExportCsvButton = nbRows > 0 && nbColumns > 0;
  if (!hasExportCsvButton) {
    return null;
  }

  return (
    <DownloadButton
      className="h-100"
      label={intl.formatMessage({ id: `form.${id}.download` as any, defaultMessage: 'Download' })}
      onDownload={onDownload}
      tabIndex={tabIndex}
    />
  );
};

ExportGridButton.displayName = 'ExportGridButton';
